/**
 * Centralized API Service
 * Uses apiClient for all requests to ensure consistent auth handling and DRY principles
 */

import { apiFetch } from "../utils/apiClient";
import { getApiBaseURLFromEnv } from "../utils/env";

export class ApiError extends Error {
	constructor(
		public readonly status: number,
		message: string,
	) {
		super(message);
		this.name = "ApiError";
	}
}

class ApiService {
	private baseUrl = getApiBaseURLFromEnv() ?? "";

	// Get base URL
	getBaseUrl(): string {
		return this.baseUrl;
	}

	// Get base URL for a specific API version (e.g., "v3")
	getBaseUrlForVersion(version: string): string {
		if (!version.startsWith("v")) {
			return this.baseUrl;
		}
		const replaced = this.baseUrl.replace(/\/api\/v\d+$/, `/api/${version}`);
		return replaced.includes(`/api/${version}`)
			? replaced
			: `${this.baseUrl}/api/${version}`;
	}

	// Generic fetch method - delegates to apiClient for auth + 401 handling
	async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
		// Prepend base URL to endpoint if not already an absolute URL
		const fullEndpoint = endpoint.startsWith("http")
			? endpoint
			: `${this.baseUrl}${endpoint}`;

		// Use apiClient which handles auth token injection and 401 responses
		const response = await apiFetch(fullEndpoint, options);

		if (!response.ok) {
			throw new ApiError(
				response.status,
				`API request failed: ${response.status} ${response.statusText}`,
			);
		}

		return response.json();
	}

	// GET request
	async get<T>(
		endpoint: string,
		additionalHeaders?: Record<string, string>,
	): Promise<T> {
		return this.fetch<T>(endpoint, {
			method: "GET",
			headers: additionalHeaders,
		});
	}

	// POST request
	async post<T>(
		endpoint: string,
		data?: any,
		additionalHeaders?: Record<string, string>,
	): Promise<T> {
		return this.fetch<T>(endpoint, {
			method: "POST",
			// JSON body needs an explicit content type: without it the browser
			// sends text/plain and FastAPI hands pydantic the raw string (422).
			headers: { "Content-Type": "application/json", ...additionalHeaders },
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	// PUT request
	async put<T>(
		endpoint: string,
		data?: any,
		additionalHeaders?: Record<string, string>,
	): Promise<T> {
		return this.fetch<T>(endpoint, {
			method: "PUT",
			headers: { "Content-Type": "application/json", ...additionalHeaders },
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	// DELETE request
	async delete<T>(
		endpoint: string,
		additionalHeaders?: Record<string, string>,
	): Promise<T> {
		return this.fetch<T>(endpoint, {
			method: "DELETE",
			headers: additionalHeaders,
		});
	}

	// Stream request (for chat streaming) - delegates to apiClient
	async stream(
		endpoint: string,
		data?: any,
		additionalHeaders?: Record<string, string>,
	): Promise<ReadableStream> {
		// Use apiClient which handles auth token injection and 401 responses
		const response = await apiFetch(endpoint, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				...additionalHeaders,
			},
			body: data ? JSON.stringify(data) : undefined,
		});

		if (!response.ok) {
			throw new Error(
				`Stream request failed: ${response.status} ${response.statusText}`,
			);
		}

		return response.body!;
	}
}

// Export singleton instance
export const apiService = new ApiService();

// Export types
export interface ProjectItem {
	id: string;
	name: string;
	type: "folder" | "file";
	children?: ProjectItem[] | null;
	created_at: string;
	updated_at: string;
	size?: number | null;
	description?: string | null;
}

export interface ProjectsResponse {
	projects: ProjectItem[];
	total: number;
}

export interface ChatMessage {
	id: string;
	role: "user" | "agent" | "system";
	content: string;
	timestamp: string;
}

export interface ChatResponse {
	content: string;
	done: boolean;
}
